import React from "react";

import { ReportLink } from "../shared/SectionCard";

import type { PsRevenueData } from "../../types";

const UNAVAILABLE_VALUE = "Not Available";
const SHOW_YOY_GROWTH = false;

const formatMetricLabel = (label: string, year?: number): string => {
  const displayYear = year ?? new Date().getFullYear();
  return `${label} ${displayYear}`;
};

const isUnavailableValue = (value: string): boolean =>
  value === UNAVAILABLE_VALUE;

const formatGrowthYoY = (value: number): string => {
  if (value === 0) {
    return "0% YoY";
  }

  const formatted = Math.abs(value).toFixed(2);
  const prefix = value > 0 ? "+" : "-";

  return `${prefix}${formatted}% YoY`;
};

const getGrowthBadgeClassName = (value: number): string => {
  if (value < 0) {
    return " admin-home-revenue__growth--negative";
  }

  if (value > 0) {
    return " admin-home-revenue__growth--positive";
  }

  return " admin-home-revenue__growth--neutral";
};

const MetricGrowthBadge: React.FC<{ growthPercent?: number }> = ({
  growthPercent,
}) => {
  if (!Number.isFinite(growthPercent)) {
    return null;
  }

  const value = growthPercent as number;

  return (
    <span
      className={`admin-home-revenue__growth${getGrowthBadgeClassName(value)}`}
    >
      {formatGrowthYoY(value)}
    </span>
  );
};

type PsRevenueCardProps = {
  data: PsRevenueData;
  isArrStaticDisabled?: boolean;
  showReportLink?: boolean;
};

const PsRevenueCard: React.FC<PsRevenueCardProps> = ({
  data,
  showReportLink = false,
}) => {
  const hasRevenueBreakdown = data.revenueBreakdown.length > 0;
  const hasArrBreakdown = data.arrBreakdown.length > 0;

  return (
    <section className="admin-home-revenue">
      <div className="admin-home-revenue__header">
        <h3 className="admin-home-revenue__title">PS Revenue</h3>
      </div>

      <div className="admin-home-revenue__columns">
        <div className="admin-home-revenue__column">
          <div className="admin-home-revenue__metric-label-row">
            <span className="admin-home-revenue__metric-label">
              {formatMetricLabel("Total Revenue", data.year)}
            </span>
            {SHOW_YOY_GROWTH ? (
              <MetricGrowthBadge growthPercent={data.revenueGrowthPercent} />
            ) : null}
          </div>

          <span
            className={`admin-home-revenue__metric-value${
              !hasRevenueBreakdown
                ? " admin-home-revenue__metric-value--no-border"
                : ""
            }${
              isUnavailableValue(data.totalRevenue)
                ? " admin-home-revenue__metric-value--unavailable"
                : ""
            }`}
          >
            {data.totalRevenue}
          </span>

          {hasRevenueBreakdown ? (
            <ul className="admin-home-revenue__breakdown">
              {data.revenueBreakdown.map((item) => (
                <li key={item.label}>
                  <span>{item.label}</span>
                  <strong
                    className={
                      isUnavailableValue(item.amount)
                        ? "admin-home-revenue__amount--unavailable"
                        : undefined
                    }
                  >
                    {item.amount}
                  </strong>
                </li>
              ))}
            </ul>
          ) : null}

          <div className="admin-home-revenue__waived">
            <span>{formatMetricLabel("Waived Rev", data.year)}</span>
            <strong
              className={
                isUnavailableValue(data.waivedRevenue)
                  ? "admin-home-revenue__amount--unavailable"
                  : undefined
              }
            >
              {data.waivedRevenue}
            </strong>
          </div>
        </div>

        <div className="admin-home-revenue__column">
          <div className="admin-home-revenue__metric-label-row">
            <span className="admin-home-revenue__metric-label">
              Live ARR
            </span>
            {SHOW_YOY_GROWTH ? (
              <MetricGrowthBadge growthPercent={data.arrGrowthPercent} />
            ) : null}
          </div>

          <span
            className={`admin-home-revenue__metric-value${
              !hasArrBreakdown
                ? " admin-home-revenue__metric-value--no-border"
                : ""
            }${
              isUnavailableValue(data.totalArr)
                ? " admin-home-revenue__metric-value--unavailable"
                : ""
            }`}
          >
            {data.totalArr}
          </span>

          {hasArrBreakdown ? (
            <ul className="admin-home-revenue__breakdown">
              {data.arrBreakdown.map((item) => (
                <li key={item.label}>
                  <span>{item.label}</span>
                  <strong
                    className={
                      isUnavailableValue(item.amount)
                        ? "admin-home-revenue__amount--unavailable"
                        : undefined
                    }
                  >
                    {item.amount}
                  </strong>
                </li>
              ))}
            </ul>
          ) : null}
        </div>
      </div>

      {showReportLink ? (
        <div className="admin-home-revenue__footer">
          <ReportLink label="View Revenue Report" />
        </div>
      ) : null}
    </section>
  );
};

export default PsRevenueCard;
